import { Stack } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/it";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { localeTextMap, useTimeData } from "src/components/monitoring/senec-time-frame-selection/TimeFrameContext";
import { TimeFrame, TimeRange } from "src/components/monitoring/commonTypes";
import { SenecDateTimePicker } from "src/components/inputs/date-picker/senec-date-time-picker/SenecDateTimePicker";
import { getValidTimeFrame } from "src/components/monitoring/senec-time-frame-selection/timeFrameUtils";
import { useScreenSize } from "src/hooks/useScreenSize";
import { DateOrTimeView } from "@mui/x-date-pickers/models";
import updateLocale from "dayjs/plugin/updateLocale";

dayjs.extend(updateLocale);

export const SenecTimeFramePicker = ({
    isLoading,
    views,
    disableFuture = true,
    onChange,
}: {
    isLoading: boolean;
    views?: DateOrTimeView[];
    disableFuture?: boolean;
    onChange?: () => void;
}) => {
    const { timeFrame, timeRange, onTimeFrameChange, onTimeRangeChange, labels, languageLocale } = useTimeData();
    const { onStartDateChanged, onEndDateChanged } = useDateTimeChange(
        timeFrame,
        timeRange,
        onTimeFrameChange,
        onTimeRangeChange,
        disableFuture,
    );
    const { isMobile } = useScreenSize();
    const timeFrameViews = views ?? undefined;
    const timeFrameFormat = views ? "DD.MM.YYYY HH:mm" : undefined;
    const disableDatePicker = timeRange === TimeRange.CUSTOM;

    dayjs.updateLocale(languageLocale, {
        weekStart: languageLocale === "en" ? 0 : 1,
    });

    const localeText = localeTextMap[languageLocale];

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={languageLocale} localeText={localeText}>
            <Stack direction={"row"} spacing={isMobile ? 1 : 2} flexWrap={"nowrap"}>
                <SenecDateTimePicker
                    onChange={(date) => {
                        date && onStartDateChanged(date);
                        onChange?.();
                    }}
                    data-testid="start-date-picker"
                    disabled={isLoading || disableDatePicker}
                    label={labels.start}
                    value={dayjs(timeFrame.start)}
                    maxDateTime={dayjs(timeFrame.end)}
                    views={timeFrameViews}
                    format={timeFrameFormat}
                    onAccept={(date) => {
                        onStartDateChanged(date);
                        onChange?.();
                    }}
                />
                <SenecDateTimePicker
                    onChange={(date) => {
                        onEndDateChanged(date);
                        onChange?.();
                    }}
                    data-testid="end-date-picker"
                    disabled={isLoading || disableDatePicker}
                    label={labels.end}
                    value={dayjs(timeFrame.end)}
                    minDateTime={dayjs(timeFrame.start)}
                    views={timeFrameViews}
                    format={timeFrameFormat}
                    disableFuture={disableFuture}
                    onAccept={(date) => {
                        onEndDateChanged(date);
                        onChange?.();
                    }}
                />
            </Stack>
        </LocalizationProvider>
    );
};

export const useDateTimeChange = (
    timeFrame: TimeFrame,
    timeRange: TimeRange,
    onTimeFrameChange: (timeFrame: TimeFrame) => void,
    onTimeRangeChange: (timeRange: TimeRange) => void,
    futureDisabled = true,
) => {
    const onStartDateChanged = (date: Dayjs | null) => {
        if (date !== null) updateTimeContext({ start: date, end: timeFrame.end });
    };

    const onEndDateChanged = (date: Dayjs | null) => {
        if (date !== null) updateTimeContext({ start: timeFrame.start, end: date });
    };

    const updateTimeContext = (newTimeFrame: TimeFrame) => {
        onTimeFrameChange(getValidTimeFrame(newTimeFrame, timeFrame, futureDisabled));

        if (timeRange !== TimeRange.FREE) {
            onTimeRangeChange(TimeRange.FREE);
        }
    };

    return {
        onStartDateChanged,
        onEndDateChanged,
    };
};
